import { Component } from '@angular/core';
import { Observable } from 'rxjs';
import { SummaryLine, SummaryResult, SummaryService, O365_MAX_LICENSES, O365_MIN_MONTHLY_SPEND } from '../../core/services/summary-service';
import { O365AddonGroup } from '../../core/utils/o365-addon-groups';
import { NavigationService } from '../../core/services/navigation-service';
import { TextService } from '../../core/services/text-service';

export interface SummaryItemGroup {
  product: SummaryLine;
  addons: SummaryLine[];
}

@Component({
  selector: 'app-summary',
  standalone: false,
  templateUrl: './summary.html',
  styleUrl: './summary.scss'
})
export class Summary {
  summary$!: Observable<SummaryResult>;

  constructor(
    public summaryService: SummaryService,
    private navigationService: NavigationService,
    public textService: TextService
  ) {
    this.summary$ = this.summaryService.summary$;
  }

  isAppsFlow(): boolean {
    return this.navigationService.getFlow() === 'apps';
  }

  getMonthlyItems(summary: SummaryResult): SummaryLine[] {
    if (!summary?.items?.length) return [];
    return summary.items.filter((item) => item.kind !== 'domain');
  }

  getMonthlyBillingTotal(summary: SummaryResult): number {
    return this.getMonthlyItems(summary)
      .filter((item) => item.billingCycle !== 'annual')
      .reduce((sum, item) => sum + item.total, 0);
  }

  getAnnualUpfrontTotal(summary: SummaryResult): number {
    return this.getMonthlyItems(summary)
      .filter((item) => item.billingCycle === 'annual')
      .reduce((sum, item) => sum + item.total, 0);
  }

  hasMonthlyBillingItems(summary: SummaryResult): boolean {
    return this.getMonthlyBillingTotal(summary) > 0;
  }

  hasAnnualUpfrontItems(summary: SummaryResult): boolean {
    return this.getAnnualUpfrontTotal(summary) > 0;
  }

  hasMixedBilling(summary: SummaryResult): boolean {
    return this.hasMonthlyBillingItems(summary) && this.hasAnnualUpfrontItems(summary);
  }

  getDueTodayTotal(summary: SummaryResult): number {
    const monthly = this.getMonthlyBillingTotal(summary);
    const annual = this.getAnnualUpfrontTotal(summary);
    let subscriptionTotal = monthly + annual;

    if (monthly > 0 && annual === 0) {
      subscriptionTotal = Math.max(10, monthly);
    }

    return subscriptionTotal + this.getDomainTotal(summary);
  }

  getBadgeTotal(summary: SummaryResult): number {
    return this.getDueTodayTotal(summary);
  }

  showMonthlyBadge(summary: SummaryResult): boolean {
    return this.hasMonthlyBillingItems(summary) && !this.hasMixedBilling(summary);
  }

  showAnnualBadge(summary: SummaryResult): boolean {
    return (
      this.hasAnnualUpfrontItems(summary) &&
      !this.hasMonthlyBillingItems(summary)
    );
  }

  showDueTodayBadge(summary: SummaryResult): boolean {
    return this.hasMixedBilling(summary);
  }

  showHeaderBadge(summary: SummaryResult): boolean {
    if (summary.items.length === 0) {
      return false;
    }
    // O365 cart already shows totals below — header badge duplicates "due today"
    return !this.isO365Flow();
  }

  getDisplayMonthlyTotal(summary: SummaryResult): number {
    return this.getMonthlyBillingTotal(summary);
  }

  hasO365MinimumMonthlySpend(summary: SummaryResult): boolean {
    const monthly = this.getMonthlyBillingTotal(summary);
    const annual = this.getAnnualUpfrontTotal(summary);
    return monthly > 0 && annual === 0 && monthly < O365_MIN_MONTHLY_SPEND;
  }

  getO365AdjustedMonthlyTotal(summary: SummaryResult): number {
    if (this.hasO365MinimumMonthlySpend(summary)) {
      return O365_MIN_MONTHLY_SPEND;
    }
    return this.getMonthlyBillingTotal(summary);
  }

  getO365DisplayDueTodayTotal(summary: SummaryResult): number {
    const monthly = this.getMonthlyBillingTotal(summary);
    const annual = this.getAnnualUpfrontTotal(summary);
    let subscriptionTotal = monthly + annual;

    if (monthly > 0 && annual === 0) {
      subscriptionTotal = Math.max(O365_MIN_MONTHLY_SPEND, monthly);
    }

    return subscriptionTotal + this.getDomainTotal(summary);
  }

  hasInvalidCartLine(cartLineId: string): boolean {
    return (
      this.summaryService.hasInvalidQuantity(cartLineId) ||
      this.summaryService.hasO365CartLineAddonQuantityExceeded(cartLineId)
    );
  }

  hasInvalidCartAddonGroup(cartLineId: string): boolean {
    return this.summaryService.hasO365CartLineAddonQuantityExceeded(cartLineId);
  }

  hasInvalidCartAddon(cartLineId: string, addon: SummaryLine): boolean {
    const addonProductId = this.getAddonProductId(addon);
    if (!addonProductId) {
      return false;
    }
    return this.summaryService.hasO365CartAddonProductQuantityExceeded(addonProductId);
  }

  getCartAddonMaxQuantity(cartLineId: string, addon: SummaryLine): number {
    const addonProductId = this.getAddonProductId(addon);
    if (!addonProductId) {
      return 1;
    }
    return this.summaryService.getO365CartAddonMaxQuantity(cartLineId, addonProductId);
  }

  getGroupedMonthlyItems(summary: SummaryResult): SummaryItemGroup[] {
    const items = this.getMonthlyItems(summary);
    const products = items.filter((item) => !this.isAddonItem(item));
    const addons = items.filter((item) => this.isAddonItem(item));
    const addonsByParent = new Map<string, SummaryLine[]>();
    const standaloneAddons: SummaryLine[] = [];

    const productKey = (item: SummaryLine) => item.cartLineId ?? item.id;

    for (const addon of addons) {
      const parentId = addon.parentProductId;
      if (parentId && products.some((product) => productKey(product) === parentId)) {
        const list = addonsByParent.get(parentId) ?? [];
        list.push(addon);
        addonsByParent.set(parentId, list);
        continue;
      }
      standaloneAddons.push(addon);
    }

    const groups: SummaryItemGroup[] = products.map((product) => ({
      product,
      addons: addonsByParent.get(productKey(product)) ?? [],
    }));

    for (const addon of standaloneAddons) {
      groups.push({ product: addon, addons: [] });
    }

    return groups;
  }

  isStandaloneAddonGroup(group: SummaryItemGroup): boolean {
    return this.isAddonItem(group.product) && group.addons.length === 0;
  }

  getDomainItems(summary: SummaryResult): SummaryLine[] {
    if (!summary?.items?.length) return [];
    return summary.items.filter((item) => item.kind === 'domain');
  }

  hasDomainItems(summary: SummaryResult): boolean {
    return !!summary?.items?.some((item) => item.kind === 'domain');
  }

  getDomainTotal(summary: SummaryResult): number {
    if (!summary?.items?.length) return 0;
    return summary.items
      .filter((item) => item.kind === 'domain')
      .reduce((acc, item) => acc + (item.total || 0), 0);
  }

  isAddonItem(item: SummaryLine): boolean {
    return item.kind === 'addon';
  }

  formatPrice(value: number): string {
    return value.toFixed(2);
  }

  isO365Flow(): boolean {
    return this.navigationService.getFlow() === 'office365';
  }

  onCartLineQuantityChange(cartLineId: string, event: Event) {
    const parsed = Number((event.target as HTMLInputElement).value);
    if (!Number.isFinite(parsed) || parsed < 1) {
      return;
    }
    this.summaryService.setO365CartLineQuantity(cartLineId, parsed);
  }

  removeCartLine(cartLineId: string, event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();
    this.summaryService.removeO365CartLine(cartLineId);
  }

  onCartAddonQuantityChange(cartLineId: string, addon: SummaryLine, event: Event) {
    const addonProductId = this.getAddonProductId(addon);
    if (!addonProductId) return;

    const parsed = Number((event.target as HTMLInputElement).value);
    if (!Number.isFinite(parsed) || parsed < 1) {
      return;
    }
    this.summaryService.setO365CartAddonQuantity(cartLineId, addonProductId, parsed);
  }

  removeCartAddon(cartLineId: string, addon: SummaryLine, event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();
    const addonProductId = this.getAddonProductId(addon);
    if (!addonProductId) return;
    this.summaryService.removeO365CartAddon(cartLineId, addonProductId);
  }

  getAddonProductId(addon: SummaryLine): string {
    if (addon.addonProductId) {
      return addon.addonProductId;
    }
    const separatorIndex = addon.id.indexOf('::');
    return separatorIndex >= 0 ? addon.id.slice(separatorIndex + 2) : '';
  }

  readonly o365MaxLicenses = O365_MAX_LICENSES;

  hasO365TotalLicenseLimitExceeded(summary: SummaryResult): boolean {
    return summary.quantity > O365_MAX_LICENSES;
  }

  trackCartGroup(_index: number, group: SummaryItemGroup): string {
    return group.product.cartLineId ?? group.product.id;
  }

  getCartAddonGroups(group: SummaryItemGroup): O365AddonGroup<SummaryLine>[] {
    return this.summaryService.groupO365SummaryAddons(group.addons);
  }

  trackCartAddonGroup(_index: number, addonGroup: O365AddonGroup<SummaryLine>): string {
    return addonGroup.groupKey;
  }

  isLastCartAddon(
    group: SummaryItemGroup,
    groupIndex: number,
    addonIndex: number
  ): boolean {
    const addonGroups = this.getCartAddonGroups(group);
    const isLastGroup = groupIndex === addonGroups.length - 1;
    const isLastInGroup = addonIndex === addonGroups[groupIndex].items.length - 1;
    return isLastGroup && isLastInGroup;
  }

  getPlaceholderText(): string {
    const flow = this.navigationService.getFlow();
    if (flow === 'office365') {
      return this.textService.getText('app_summary_placeholder');
    }
    return this.textService.getText('apps_summary_placeholder');
  }
}
